import { useState } from "react";
import { ActivityIndicator, Checkbox, Text } from "react-native-paper";
import { ActionsContainer, Button, ScreenContainer, ScrollableContainer } from "../../components";
import { useMutation, useQuery } from "@apollo/client";
import { EVENT_DETAILS_QUERY } from "./EventDetailsScreen";
import {
  CreateEventRegistrationMutation,
  EventDetailsQuery,
  EventEligibilityQuery,
  EventRegistrationQuery,
  FormData,
} from "./types";
import { Pressable, TouchableOpacity, View } from "react-native";
import { readableDate } from "../../utils/dateUtils";
import { cond, stubTrue } from "lodash";
import { CREATE_EVENT_REGISTRATION, EVENT_ELIGIBILITY_QUERY, EVENT_REGISTRATION_QUERY } from "./queries";
import { afterRegistrationClose, beforeRegistrationOpen } from "./helpers";
import { Controller, UseFormHandleSubmit, useForm } from "react-hook-form";
import HTMLDisplay from "../../components/HTMLDisplay";
import { colors } from "../../../theme";
import { ScreenProps } from "../types";

const defaultValues: FormData = {
  sessions: [],
  eventId: "",
  acceptedTerms: false,
};

export const ButtonContainer = ({
  event,
  registrationEligible,
  onSubmit,
  handleSubmit,
  isSubmitting,
  watch,
  invalidError,
  setInvalidError,
  isRegistered,
}: {
  event: EventDetailsQuery["event"];
  registrationEligible: boolean;
  handleSubmit: UseFormHandleSubmit<FormData>;
  onSubmit: (data: FormData) => void;
  isSubmitting: boolean;
  watch: (field?: keyof FormData) => any;
  invalidError: string;
  setInvalidError: (error: string) => void;
  isRegistered: boolean;
}) => {
  const isTermsContentAvailable = event?.termsContent;
  const isTermsAccepted = watch("acceptedTerms");
  const isSessionSelected = watch("sessions").length;
  const isValid = isTermsContentAvailable ? isTermsAccepted && isSessionSelected : isSessionSelected;

  const registerButton = cond([
    [
      () => isRegistered,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Already Registered
          </Button>
        </View>
      ),
    ],
    [
      beforeRegistrationOpen,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Opens {readableDate(new Date(event.registrationOpenAt))}
        </Button>
      ),
    ],
    [
      afterRegistrationClose,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Closed
        </Button>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Registration Unavailable
          </Button>
        </View>
      ),
    ],
    [
      stubTrue,
      () => (
        <Button
          mode="contained"
          isValid={isValid}
          loading={isSubmitting}
          disabled={isSubmitting}
          onPress={handleSubmit(onSubmit)}
          testID="register-button"
        >
          Register
        </Button>
      ),
    ],
  ]);
  return (
    <ActionsContainer
      errorAlert={!!invalidError}
      errorTitle="Missing Required Information"
      errorMessage={invalidError}
      onCloseErrorMessage={() => setInvalidError("")}
    >
      {registerButton(event)}
    </ActionsContainer>
  );
};

const AgreementCheckbox = ({
  onChange,
  value,
  event,
}: {
  onChange: (value: boolean) => void;
  value: boolean;
  event: EventDetailsQuery["event"];
}) => (
  <Pressable
    className="flex flex-row items-start"
    onPress={() => {
      onChange(!value);
    }}
  >
    <Checkbox.Android status={value ? "checked" : "unchecked"} />
    <View className="flex-shrink">
      <Text className="items-start mt-1 ml-2 flex-shrink">
        <HTMLDisplay html={event?.termsContent} fontSize={16} lineHeight={24} color={colors.ink.dark} />
      </Text>
    </View>
  </Pressable>
);

export default function ({ navigation, route }: ScreenProps<"EventRegistration">) {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [invalidError, setInvalidError] = useState<string>("");

  const { loading, data } = useQuery<EventDetailsQuery>(EVENT_DETAILS_QUERY, {
    variables: { slug: route.params.slug },
  });

  const { data: registrationData, loading: registrationLoading } = useQuery<EventRegistrationQuery>(EVENT_REGISTRATION_QUERY, {
    variables: { slug: route.params.slug },
  });

  const {
    watch,
    trigger,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm({ defaultValues, mode: "onChange" });

  const [createEventRegistration] = useMutation<CreateEventRegistrationMutation>(CREATE_EVENT_REGISTRATION);

  const { data: eligibilityData } = useQuery<EventEligibilityQuery>(EVENT_ELIGIBILITY_QUERY, {
    variables: { slug: route.params.slug },
  });
  const registrationEligible = !!eligibilityData?.eventEligibility?.isEligible;

  if (loading || registrationLoading) {
    return <ActivityIndicator className="flex-1 items-center justify-center" testID="query-loader" />;
  }

  const isRegistered = !!registrationData?.eventRegistration?.registeredAt;

  if (isRegistered) {
    navigation.navigate("EventConfirmation", { slug: route.params.slug });
  }

  const { event } = data || {};

  if (event?.id) {
    setValue("eventId", event?.id);
  }

  const onSubmit = async (data: FormData) => {
    const shouldAcceptTerms = event?.termsContent;
    const hasAcceptedTerms = data.acceptedTerms;
    const hasSelectedSessions = data.sessions.length > 0;

    if ((shouldAcceptTerms && !(hasAcceptedTerms && hasSelectedSessions)) || !hasSelectedSessions) {
      const errorMessage = shouldAcceptTerms
        ? "Please accept the terms and select at least one session to attend."
        : "Please select at least one session to attend.";
      setInvalidError(errorMessage);
      return;
    }

    await createEventRegistration({ variables: { input: data } });
    navigation.navigate("EventConfirmation", { slug: route.params.slug });
  };

  const onPress = async ({ id }: { id: string }) => {
    const newCheckedState = !checkedItems[id];
    setCheckedItems({ ...checkedItems, [id]: newCheckedState });

    const currentSessions = getValues("sessions");

    if (newCheckedState) {
      setValue("sessions", [...currentSessions, { id }]);
    } else {
      setValue(
        "sessions",
        currentSessions.filter((session) => session.id !== id)
      );
    }
    await trigger("sessions");
  };

  return (
    <ScreenContainer navigationTitle="Event Registration">
      <ScrollableContainer>
        <View className="m-6">
          <Text className="text-title-5 font-700">{event?.title}</Text>
          <Text className="text-regular-normal mt-1">{event?.location}</Text>
          <Text className="text-regular-normal mt-1">
            {readableDate(event?.startsAt)} - {readableDate(event?.endsAt)}
          </Text>
          <Text
            className="text-regular-normal underline text-primary mt-1"
            onPress={() =>
              navigation.navigate("EventDetails", {
                slug: route.params.slug,
              })
            }
          >
            View Event Details
          </Text>
          <View className="bg-sky-lightest h-1 mt-6" />
          <Text className="text-large-normal mt-6 font-700">Planned Attendance</Text>
          <Text className="text-regular-normal mt-2">Select the day(s) you plan to attend.</Text>
          <View className="mt-6">
            {event?.sessions?.map(({ id, startsAt, title }: { id: string; startsAt: string; title: string }) => {
              return (
                <Controller
                  name={id as any}
                  control={control}
                  key={id}
                  render={() => {
                    return (
                      <TouchableOpacity
                        key={id}
                        testID={`session-${id}`}
                        activeOpacity={0.7}
                        onPress={() => {
                          onPress({ id });
                        }}
                        className={`flex flex-row rounded-lg mb-3 ${
                          checkedItems[id] ? "border-primary-lighter border-2 p-3" : "border-sky-dark border p-[13px]"
                        }`}
                      >
                        <View className="self-center mr-2">
                          <Checkbox.Android status={checkedItems[id] ? "checked" : "unchecked"} />
                        </View>
                        <View className="flex-1">
                          <Text className="font-700 text-regular-tight text-ink-darkest mb-1">{title}</Text>
                          <Text>{readableDate(startsAt)}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  }}
                />
              );
            })}
          </View>
          {event?.termsContent && (
            <View>
              <View className="bg-sky-lightest h-1 mt-6" />
              <View className="my-6">
                <Controller
                  name="acceptedTerms"
                  control={control}
                  defaultValue={false}
                  render={({ field: { onChange, value } }) => <AgreementCheckbox {...{ onChange, value, event }} />}
                />
              </View>
            </View>
          )}
        </View>
        {event && (
          <ButtonContainer
            {...{
              event,
              registrationEligible,
              isSubmitting,
              handleSubmit,
              onSubmit,
              watch,
              invalidError,
              setInvalidError,
              isRegistered,
            }}
          />
        )}
      </ScrollableContainer>
    </ScreenContainer>
  );
}
